import { useState, useMemo } from 'react';
import Pagination from '../../../components/shared/Pagination';
import ConfirmDialog from '../../../components/shared/ConfirmDialog';
import SuccessDialog from '../../../components/shared/SuccessDialog';
import FundDisbursementsTable from '../../../components/finance/fundDisbursements/FundDisbursementsTable';
import FundDisbursementFormModal from '../../../components/finance/fundDisbursements/FundDisbursementFormModal';
import FundDisbursementsFilters from '../../../components/finance/fundDisbursements/FundDisbursementsFilters';
import { useListFilters } from '../../../hooks/shared/useListFilters';
import { useCrudModals } from '../../../hooks/shared/useCrudModals';
import { useFundDisbursements } from '../../../hooks/finance/useFundDisbursements';
import { useFundDisbursementMutations } from '../../../hooks/finance/useFundDisbursementMutations';
import {
  PAGE,
  ACTIONS,
  MESSAGES,
  CONFIRM,
  SUCCESS_DIALOG,
} from './FundDisbursementsConstants';

const FundDisbursementsPage = () => {
  const [fundDisbursementFilter, setFundDisbursementFilter] = useState('');

  const listFilters = useListFilters(10);
  const { filters: baseFilters, setPage, setPageToOne } = listFilters;

  const apiFilters = useMemo(() => {
    const f = { ...baseFilters };
    if (fundDisbursementFilter) f.fundDisbursement = fundDisbursementFilter;
    return f;
  }, [baseFilters, fundDisbursementFilter]);

  const { data: fundDisbursements, isLoading: isLoadingFundDisbursements } =
    useFundDisbursements({ page: 1, pageSize: 200 }, { enabled: true });

  const { data, pagination, isLoading, isFetching, isError, error } =
    useFundDisbursements(apiFilters, { enabled: true });

  const { createMutation, updateMutation, deleteMutation } =
    useFundDisbursementMutations();

  const crud = useCrudModals({
    createMutation,
    updateMutation,
    deleteMutation,
    getItemId: (item) => item._id,
    getItemName: (item) =>
      item.amount != null
        ? `Amount: ${item.amount} - ${item.fundDisbursement?.contract?.contractNo || item._id}`
        : item._id,
    messages: MESSAGES,
  });

  const {
    form,
    confirm,
    success,
    deletingId,
    deleteMutation: deleteMut,
    handleDeleteRequest,
  } = crud;

  return (
    <div className="rounded-2xl border border-cream/60 bg-white shadow-sm">
      <FundDisbursementsFilters
        title={PAGE.title}
        description={PAGE.description}
        fundDisbursement={fundDisbursementFilter}
        onFundDisbursementChange={setFundDisbursementFilter}
        onFilterChange={setPageToOne}
        fundDisbursements={fundDisbursements}
        isLoadingFundDisbursements={isLoadingFundDisbursements}
        primaryAction={{
          label: ACTIONS.create,
          onClick: form.openCreateModal,
          disabled: form.isSubmitting,
        }}
      >
        <FundDisbursementsTable
          data={data}
          isLoading={isLoading}
          isFetching={isFetching}
          isError={isError}
          error={error}
          onEdit={form.openEditModal}
          onDelete={handleDeleteRequest}
          isBusy={form.isSubmitting || deleteMut.isPending}
          deletingId={deletingId}
        />
        <Pagination
          pagination={pagination}
          onPageChange={(newPage) => setPage(newPage)}
        />
      </FundDisbursementsFilters>

      <FundDisbursementFormModal
        isOpen={form.isFormOpen}
        mode={form.formMode}
        fundDisbursement={form.selectedItem}
        onSubmit={form.handleSubmitForm}
        onClose={form.handleCloseForm}
        isSubmitting={form.isSubmitting}
        serverError={form.formError}
      />

      <ConfirmDialog
        isOpen={confirm.isOpen}
        title={confirm.type === 'delete' ? CONFIRM.deleteTitle : CONFIRM.updateTitle}
        message={confirm.type === 'delete' ? CONFIRM.deleteMessage(confirm.data?.name) : CONFIRM.updateMessage}
        confirmText={confirm.type === 'delete' ? ACTIONS.delete : ACTIONS.edit}
        cancelText={ACTIONS.cancel}
        onConfirm={confirm.onConfirm}
        onCancel={confirm.onCancel}
        isLoading={confirm.isLoading}
      />

      <SuccessDialog
        isOpen={success.isOpen}
        title={success.variant === 'error' ? SUCCESS_DIALOG.errorTitle : SUCCESS_DIALOG.successTitle}
        message={success.message}
        onClose={success.onClose}
        variant={success.variant}
      />
    </div>
  );
};

export default FundDisbursementsPage;
